import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'

import TextInput from '@/components/ui/inputs/TextInput'

import StaffSelectionTable, { StaffData } from './StaffSelectionTable'

export type MeetingLinkStaffsProps = {
  addingStaffs: StaffData[]
  isAdding?: boolean
  meetingLinkStaffs: StaffData[]
  onAddMeetingLinkStaffs?: (addingStaffIds: string[]) => void
  onDeleteMeetingLinkStaffs?: (meetingLinkStaffIds: string[]) => void
  onOpen?: () => void
  onSearchStaffs?: (text: string) => void
  readOnly?: boolean
}

export function MeetingLinkStaffs(props: MeetingLinkStaffsProps) {
  const {
    addingStaffs,
    isAdding = false,
    meetingLinkStaffs,
    onAddMeetingLinkStaffs,
    onDeleteMeetingLinkStaffs,
    onOpen,
    onSearchStaffs,
    readOnly = false,
  } = props
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [selectedAddingStaffIds, setSelectedAddingStaffIds] = useState<string[]>([])
  const [selectedMeetingLinkStaffIds, setSelectedMeetingLinkStaffIds] = useState<string[]>([])

  useEffect(() => {
    if (!selectedAddingStaffIds.length) return

    if (!!_.intersection(_.map(meetingLinkStaffs, 'id'), selectedAddingStaffIds).length) {
      setOpen(false)
      setSelectedAddingStaffIds([])
    }
  }, [meetingLinkStaffs, selectedAddingStaffIds])

  useEffect(() => {
    if (!selectedMeetingLinkStaffIds.length) return

    if (!_.intersection(_.map(meetingLinkStaffs, 'id'), selectedMeetingLinkStaffIds).length) {
      setSelectedMeetingLinkStaffIds([])
    }
  }, [meetingLinkStaffs, selectedMeetingLinkStaffIds])

  const handleCloseDialog = () => {
    setOpen(false)
    setSelectedAddingStaffIds([])
  }

  const handleOpenDialog = () => {
    setOpen(true)
    onOpen?.()
  }

  const handleAddMeetingLinkStaffs = () => {
    onAddMeetingLinkStaffs?.(selectedAddingStaffIds)
  }

  const handleDeleteMeetingLinkStaffs = () => {
    onDeleteMeetingLinkStaffs?.(selectedMeetingLinkStaffIds)
  }

  const handleSearchStaffs = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchStaffs?.(event.target.value)
  }

  const handleSelectAddingStaffs = (ids: string[]) => {
    setSelectedAddingStaffIds(ids)
  }

  const handleSelectMeetingLinkStaffs = (ids: string[]) => {
    setSelectedMeetingLinkStaffIds(ids)
  }

  return (
    <Stack spacing={2}>
      {!readOnly && (
        <Stack direction="row" justifyContent="end" spacing={1}>
          <Button onClick={handleOpenDialog} startIcon={<GroupAddRoundedIcon />} variant="contained">
            {t('page.meetingLink.meetingLinkStaffs.addStaffButton.label')}
          </Button>
          <Button
            color="error"
            disabled={!selectedMeetingLinkStaffIds.length}
            onClick={handleDeleteMeetingLinkStaffs}
            startIcon={<DeleteRoundedIcon />}
            variant="contained"
          >
            {t('common:button.delete.label')}
          </Button>
        </Stack>
      )}
      <StaffSelectionTable data={meetingLinkStaffs} onSelect={handleSelectMeetingLinkStaffs} selected={selectedMeetingLinkStaffIds} readOnly={readOnly} />
      {!readOnly && (
        <Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseDialog}>
          <DialogContent>
            <Stack spacing={2}>
              <TextInput onChange={handleSearchStaffs} placeholder={t('page.meetingLink.meetingLinkStaffs.searchStaffsInput.placeholder')} />
              <StaffSelectionTable data={addingStaffs} onSelect={handleSelectAddingStaffs} selected={selectedAddingStaffIds} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} variant="neutral">
              {t('common:button.close.label')}
            </Button>
            <LoadingButton loading={isAdding} onClick={handleAddMeetingLinkStaffs} variant="contained">
              {t('common:button.add.label')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  )
}

export default MeetingLinkStaffs
